import { useQuery } from '@apollo/client';
import { AddressFile } from '@provision/entity-types';
import { QueryHookOptions } from '@provision/graphql-client';
import { useMemo } from 'react';
import { GET_ADDRESS_FILE_QUERY } from 'src/graphql/entities/address-file/address-file.operations';
import { ConditionInput, QueryVariables, Records, SortingInput } from 'src/graphql/types';
import { useLoadMore } from 'src/graphql/utils';
import { useNotifications } from 'src/utils/hooks';
import { useCacheManager } from 'src/utils/hooks/use-cache-manager';

export type GetAddressFilesQuery = {
  getAddressFile: Records<AddressFile>;
};

export type GetAddressFilesParams = {
  filters?: ConditionInput[];
  sorting?: SortingInput[];
  limit?: number;
  offset?: number;
  includeDeleted?: boolean;
};

export const ADDRESS_FILE_DEFAULT_PAGE_SIZE = 10;

export const getAddressFilesQueryParams = (
  params: GetAddressFilesParams = {},
): GetAddressFilesParams => ({
  ...params,
  limit: ADDRESS_FILE_DEFAULT_PAGE_SIZE,
  sorting: [
    { attribute: 'pinnedAt', desc: true },
    { attribute: 'createdAt', desc: true },
  ],
});

export const useAddressFiles = (
  params: GetAddressFilesParams = {},
  options: QueryHookOptions<GetAddressFilesQuery> = {},
) => {
  const { showErrorAlert } = useNotifications();

  const queryResult = useQuery<GetAddressFilesQuery, QueryVariables>(GET_ADDRESS_FILE_QUERY, {
    ...options,
    variables: {
      params: getAddressFilesQueryParams(params),
    },
    skip: !params?.filters?.length,
    onError: (error) => {
      console.error(error);
      showErrorAlert('Failed to fetch address files, please try again');
    },
  });

  const {
    data,
    loading: isLoading,
    fetchMore,
    variables,
  } = queryResult;

  const { reset } = useCacheManager<GetAddressFilesQuery, QueryVariables>(
    GET_ADDRESS_FILE_QUERY,
    queryResult,
  );

  const records = useMemo(
    () => sortAddressFiles(data?.getAddressFile?.records || []),
    [data?.getAddressFile?.records],
  );

  const totalRecords = data?.getAddressFile?.metadata?.totalRecords ?? 0;

  const { loadMore, hasReachedEnd } = useLoadMore<GetAddressFilesQuery>({
    offset: records?.length ?? 0,
    fetchMore,
    params: variables?.params!,
  });

  return {
    addressFiles: records,
    isLoading,
    loadMore,
    resetCache: reset,
    totalRecords,
    hasMore: totalRecords ? records?.length < totalRecords : !hasReachedEnd,
  };
};

function sortAddressFiles(files: AddressFile[]): AddressFile[] {
  return files.slice().sort((a, b) => {
    const aPinned = Boolean(a.pinnedAt);
    const bPinned = Boolean(b.pinnedAt);

    if (aPinned && !bPinned) return -1;
    if (!aPinned && bPinned) return 1;

    // Both are either pinned or unpinned — sort by createdAt descending
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
}
